import { listItems } from '@/modules/inventory/services';
import { listReservations, listReservationsInRange } from '@/modules/reservations/services';
import type { Reservation } from '@/modules/reservations/types';
import { todayIsoDate } from '@/modules/reservations/utils/dateRange';
import { isBlockingReservation } from '@/modules/reservations/utils/reservationFilters';
import { addDays } from '@/modules/calendar/utils/dateRanges';

export type CalendarDashboardStats = {
  todayActivities: number;
  upcomingReservations: number;
  itemsAffectedToday: number;
  expiringSoon: number;
  highOccupancyItems: Array<{ itemId: string; label: string; count: number }>;
};

function isActiveOnDate(reservation: Reservation, date: string): boolean {
  return (
    reservation.status !== 'cancelled' &&
    reservation.startDate <= date &&
    reservation.endDate >= date
  );
}

function isEndingSoon(reservation: Reservation, today: string, withinDays: number): boolean {
  const until = addDays(today, withinDays);
  return (
    reservation.status !== 'cancelled' &&
    reservation.status !== 'completed' &&
    reservation.endDate >= today &&
    reservation.endDate <= until
  );
}

export async function fetchCalendarDashboardStats(): Promise<CalendarDashboardStats> {
  const today = todayIsoDate();
  const horizonEnd = addDays(today, 30);
  const [allReservations, rangeReservations, items] = await Promise.all([
    listReservations(),
    listReservationsInRange(today, horizonEnd),
    listItems(),
  ]);

  const todayActivities = allReservations.filter((r) => isActiveOnDate(r, today));
  const upcoming = allReservations.filter(
    (r) =>
      r.startDate > today &&
      r.status !== 'cancelled' &&
      r.startDate <= addDays(today, 14),
  );

  const affectedIds = new Set<string>();
  for (const r of todayActivities) {
    for (const id of r.inventoryItemIds) affectedIds.add(id);
  }

  const expiringSoon = allReservations.filter((r) => isEndingSoon(r, today, 7));

  const itemCounts = new Map<string, number>();
  for (const r of rangeReservations) {
    if (!isBlockingReservation(r) && r.status !== 'draft') continue;
    if (r.status === 'cancelled' || r.status === 'completed') continue;
    for (const itemId of r.inventoryItemIds) {
      itemCounts.set(itemId, (itemCounts.get(itemId) ?? 0) + 1);
    }
  }

  const itemsById = new Map(items.map((i) => [i.id, i]));
  const highOccupancyItems = [...itemCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([itemId, count]) => ({
      itemId,
      count,
      label: itemsById.get(itemId)?.name ?? itemId,
    }));

  return {
    todayActivities: todayActivities.length,
    upcomingReservations: upcoming.length,
    itemsAffectedToday: affectedIds.size,
    expiringSoon: expiringSoon.length,
    highOccupancyItems,
  };
}
